import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import {
    getProduct, createProduct, updateProduct,
    createVariant, updateVariant, deleteVariant,
    getCategories, createCategory, getProductMovements,
    uploadProductImage, deleteProductImage
} from '../services/api';
import {
    ArrowLeft, Copy, X, Save, Plus, Edit2, Trash2, Package,
    FileText, DollarSign, Layers, BarChart3,
    AlertCircle, Upload, Info
} from 'lucide-react';
import { toast } from 'sonner';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const TABS = [
    { id: 'general', label: 'General', icon: FileText },
    { id: 'pricing', label: 'Pricing', icon: DollarSign },
    { id: 'variants', label: 'Variants', icon: Layers },
    { id: 'history', label: 'History', icon: BarChart3 },
];

export default function ProductDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isNew = !id || id === 'new';
    const fileInputRef = useRef(null);

    const [activeTab, setActiveTab] = useState('general');
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [newCat, setNewCat] = useState('');
    const [movements, setMovements] = useState([]);
    const [variants, setVariants] = useState([]);
    const [currentStock, setCurrentStock] = useState(0);
    const [isLowStock, setIsLowStock] = useState(false);
    const [imageUrl, setImageUrl] = useState(null);
    const [pendingFile, setPendingFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);

    const [form, setForm] = useState({
        name: '', reference: '', barcode: '', hs_code: '', category: '',
        weight: '', weight_unit: 'kg', price: '', purchase_price: '',
        price_a: '', price_b: '', price_c: '',
        low_stock_threshold: '10', notes: '', is_pack_based: false,
        pack_content: '', boxes_per_pallet: ''
    });

    const [showVariantModal, setShowVariantModal] = useState(false);
    const [editingVariant, setEditingVariant] = useState(null);
    const [variantForm, setVariantForm] = useState({ name: '', sku: '', barcode: '', price: '' });
    const [variantSaving, setVariantSaving] = useState(false);

    useEffect(() => {
        getCategories().then(r => setCategories(r.data)).catch(() => {});
        if (!isNew) loadProduct();
    }, [id]);

    useEffect(() => {
        return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
    }, [previewUrl]);

    const loadProduct = async () => {
        try {
            const res = await getProduct(id);
            const p = res.data;
            setForm({
                name: p.name || '',
                reference: p.reference || '',
                barcode: p.barcode || '',
                hs_code: p.hs_code || '',
                category: p.category || '',
                weight: p.weight != null ? p.weight.toString() : '',
                weight_unit: p.weight_unit || 'kg',
                price: p.price != null ? p.price.toString() : '',
                purchase_price: p.purchase_price != null ? p.purchase_price.toString() : '',
                price_a: p.price_a != null ? p.price_a.toString() : '',
                price_b: p.price_b != null ? p.price_b.toString() : '',
                price_c: p.price_c != null ? p.price_c.toString() : '',
                low_stock_threshold: p.low_stock_threshold?.toString() || '10',
                notes: p.notes || '',
                is_pack_based: p.is_pack_based || false,
                pack_content: p.pack_content != null ? p.pack_content.toString() : '',
                boxes_per_pallet: p.boxes_per_pallet != null ? p.boxes_per_pallet.toString() : '',
            });
            setImageUrl(p.image_url || null);
            setVariants(p.variants || []);
            setCurrentStock(p.current_stock || 0);
            setIsLowStock((p.current_stock || 0) < (p.low_stock_threshold || 10));
            getProductMovements(id).then(r => setMovements(r.data)).catch(() => {});
        } catch {
            toast.error('Product not found');
            navigate('/products');
        } finally {
            setLoading(false);
        }
    };

    const set = (key, val) => setForm(p => ({ ...p, [key]: val }));
    const packQty = parseInt(form.pack_content) || 1;
    const isPack = form.is_pack_based && packQty > 1;

    const unitPrice = (packPrice) => {
        if (!isPack || !packPrice) return null;
        return (parseFloat(packPrice) / packQty).toFixed(2);
    };

    const handleSave = async () => {
        if (!form.name.trim()) return toast.error('Product name is required');
        setSaving(true);
        try {
            const data = {
                name: form.name,
                reference: form.reference || null,
                barcode: form.barcode || null,
                hs_code: form.hs_code || null,
                category: form.category || null,
                weight: form.weight ? parseFloat(form.weight) : null,
                weight_unit: form.weight_unit,
                price: parseFloat(form.price) || 0,
                purchase_price: form.purchase_price ? parseFloat(form.purchase_price) : null,
                price_a: form.price_a ? parseFloat(form.price_a) : null,
                price_b: form.price_b ? parseFloat(form.price_b) : null,
                price_c: form.price_c ? parseFloat(form.price_c) : null,
                low_stock_threshold: parseInt(form.low_stock_threshold) || 10,
                notes: form.notes || null,
                is_pack_based: form.is_pack_based,
                pack_content: form.is_pack_based && form.pack_content ? parseInt(form.pack_content) : null,
                boxes_per_pallet: form.boxes_per_pallet ? parseInt(form.boxes_per_pallet) : null,
            };
            let productId = id;
            if (isNew) {
                const res = await createProduct(data);
                productId = res.data.id;
                toast.success('Product created');
            } else {
                await updateProduct(id, data);
                toast.success('Product saved');
            }
            // Upload pending image
            if (pendingFile && productId) {
                try {
                    await uploadProductImage(productId, pendingFile);
                    setPendingFile(null);
                    setPreviewUrl(null);
                } catch { toast.error('Image upload failed'); }
            }
            if (isNew) {
                navigate(`/products/${productId}`, { replace: true });
            } else {
                loadProduct();
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    const handleDuplicate = async () => {
        if (!form.name.trim()) return;
        setSaving(true);
        try {
            const data = {
                name: form.name + ' (Copy)',
                reference: form.reference || null, barcode: null,
                hs_code: form.hs_code || null, category: form.category || null,
                weight: form.weight ? parseFloat(form.weight) : null, weight_unit: form.weight_unit,
                price: parseFloat(form.price) || 0,
                purchase_price: form.purchase_price ? parseFloat(form.purchase_price) : null,
                price_a: form.price_a ? parseFloat(form.price_a) : null,
                price_b: form.price_b ? parseFloat(form.price_b) : null,
                price_c: form.price_c ? parseFloat(form.price_c) : null,
                low_stock_threshold: parseInt(form.low_stock_threshold) || 10,
                notes: form.notes || null, is_pack_based: form.is_pack_based,
                pack_content: form.pack_content ? parseInt(form.pack_content) : null,
                boxes_per_pallet: form.boxes_per_pallet ? parseInt(form.boxes_per_pallet) : null,
            };
            const res = await createProduct(data);
            toast.success('Product duplicated');
            navigate(`/products/${res.data.id}`);
        } catch { toast.error('Failed to duplicate'); }
        finally { setSaving(false); }
    };

    const handleAddCategory = async () => {
        if (!newCat.trim()) return;
        try {
            await createCategory({ name: newCat });
            setNewCat('');
            setCategories((await getCategories()).data);
            toast.success('Category added');
        } catch { toast.error('Failed to add category'); }
    };

    const handleImageSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (isNew) {
            // Store for later upload after save
            setPendingFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        } else {
            // Upload immediately for existing product
            setUploading(true);
            uploadProductImage(id, file)
                .then(res => { setImageUrl(res.data.image_url); toast.success('Image uploaded'); })
                .catch(() => toast.error('Upload failed'))
                .finally(() => setUploading(false));
        }
    };

    const displayImage = previewUrl || (imageUrl ? `${API_BASE}${imageUrl}` : null);

    // Variant handlers
    const openVariantModal = (v = null) => {
        if (v) {
            setEditingVariant(v);
            setVariantForm({ name: v.name, sku: v.sku || '', barcode: v.barcode || '', price: v.price != null ? v.price.toString() : '' });
        } else {
            setEditingVariant(null);
            setVariantForm({ name: '', sku: '', barcode: '', price: '' });
        }
        setShowVariantModal(true);
    };

    const handleVariantSubmit = async (e) => {
        e.preventDefault();
        if (!variantForm.name.trim()) return toast.error('Name is required');
        setVariantSaving(true);
        try {
            const data = { name: variantForm.name, sku: variantForm.sku || null, barcode: variantForm.barcode || null, price: variantForm.price ? parseFloat(variantForm.price) : null, attributes: {} };
            if (editingVariant) { await updateVariant(id, editingVariant.id, data); toast.success('Variant updated'); }
            else { await createVariant(id, data); toast.success('Variant created'); }
            setShowVariantModal(false);
            loadProduct();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
        finally { setVariantSaving(false); }
    };

    const handleDeleteVariant = async (vid) => {
        if (!window.confirm('Delete this variant?')) return;
        try { await deleteVariant(id, vid); toast.success('Deleted'); loadProduct(); }
        catch { toast.error('Failed to delete'); }
    };

    if (loading) return (
        <DashboardLayout>
            <div className="flex items-center justify-center py-20">
                <div className="w-6 h-6 border-2 border-gray-200 border-t-gray-600 rounded-full animate-spin" />
            </div>
        </DashboardLayout>
    );

    return (
        <DashboardLayout>
            <div data-testid="product-detail-page">
                {/* Header */}
                <div className="flex items-center justify-between pb-4 mb-6 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <button onClick={() => navigate('/products')} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" data-testid="back-to-products">
                            <ArrowLeft size={20} className="text-gray-500" />
                        </button>
                        <h1 className="text-xl font-semibold text-gray-900 tracking-tight">
                            {isNew ? 'New Product' : form.name || 'Untitled'}
                        </h1>
                    </div>
                    <div className="flex items-center gap-2">
                        {!isNew && (
                            <button onClick={handleDuplicate} disabled={saving} className="btn btn-secondary text-sm" data-testid="duplicate-btn">
                                <Copy size={15} /> Duplicate
                            </button>
                        )}
                        <button onClick={() => navigate('/products')} className="btn btn-secondary text-sm" data-testid="cancel-btn">
                            <X size={15} /> Cancel
                        </button>
                        <button onClick={handleSave} disabled={saving} className="btn btn-primary text-sm" data-testid="save-btn">
                            <Save size={15} /> {saving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>

                <div className="flex gap-6">
                    {/* Left Column */}
                    <div className="w-80 flex-shrink-0 space-y-5">
                        <div className="card">
                            <div className="text-xs font-medium text-gray-500 mb-3">Product Image</div>
                            <input type="file" ref={fileInputRef} className="hidden" accept="image/jpeg,image/png,image/webp" onChange={handleImageSelect} data-testid="image-file-input" />
                            <div className="relative">
                                <div onClick={() => fileInputRef.current?.click()} className="aspect-square rounded-xl bg-gray-50 border border-dashed border-gray-200 flex flex-col items-center justify-center text-gray-400 cursor-pointer hover:border-gray-300 transition-colors overflow-hidden" data-testid="image-upload-area">
                                    {uploading ? (
                                        <div className="w-6 h-6 border-2 border-gray-200 border-t-gray-600 rounded-full animate-spin" />
                                    ) : displayImage ? (
                                        <img src={displayImage} alt={form.name} className="w-full h-full object-cover" data-testid="product-image" onError={e => { e.target.style.display = 'none'; }} />
                                    ) : (
                                        <>
                                            <Upload size={28} className="mb-2 opacity-40" />
                                            <span className="text-xs">Click to upload</span>
                                        </>
                                    )}
                                </div>
                                {displayImage && !isNew && (
                                    <button onClick={async (e) => { e.stopPropagation(); if (!window.confirm('Foto silinsin mi?')) return; try { await deleteProductImage(id); setForm(f => ({...f, image_url: null})); setPendingFile(null); toast.success('Foto silindi'); } catch { toast.error('Silinemedi'); }}} className="absolute top-2 right-2 w-7 h-7 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg transition-colors" data-testid="delete-image-btn">
                                        <X size={14} />
                                    </button>
                                )}
                            </div>
                            {pendingFile && <p className="text-xs text-amber-600 mt-2">Image will be uploaded on save</p>}
                        </div>
                        {!isNew && (
                            <div className="card">
                                <span className="text-xs font-medium text-gray-500">Current Stock</span>
                                <div className="text-center py-2">
                                    <div className={`text-4xl font-bold ${isLowStock ? 'text-red-500' : 'text-gray-900'}`}>{currentStock}</div>
                                    {isLowStock && (
                                        <div className="flex items-center justify-center gap-1 mt-2 text-red-500">
                                            <AlertCircle size={14} /><span className="text-xs font-medium">Low Stock</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right Column */}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1 mb-5">
                            {TABS.map(tab => {
                                const Icon = tab.icon;
                                if (isNew && ['variants', 'barcode', 'history'].includes(tab.id)) return null;
                                return (
                                    <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                                        className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-all ${activeTab === tab.id ? 'bg-gray-100 text-gray-900' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'}`}
                                        data-testid={`tab-${tab.id}`}>
                                        <Icon size={15} />{tab.label}
                                    </button>
                                );
                            })}
                        </div>

                        <div className="card">
                            {/* GENERAL */}
                            {activeTab === 'general' && (
                                <div className="space-y-5" data-testid="tab-general-content">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div><label className="form-label">Product Name *</label><input type="text" value={form.name} onChange={e => set('name', e.target.value)} className="form-input" required data-testid="product-name-input" /></div>
                                        <div><label className="form-label">Reference</label><input type="text" value={form.reference} onChange={e => set('reference', e.target.value)} className="form-input" data-testid="product-reference-input" /></div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div><label className="form-label">Barcode</label><input type="text" value={form.barcode} onChange={e => set('barcode', e.target.value)} className="form-input" data-testid="product-barcode-input" /></div>
                                        <div><label className="form-label">HS Code</label><input type="text" value={form.hs_code} onChange={e => set('hs_code', e.target.value)} className="form-input" data-testid="product-hscode-input" /></div>
                                    </div>
                                    <div>
                                        <label className="form-label">Category</label>
                                        <select value={form.category} onChange={e => set('category', e.target.value)} className="form-select" data-testid="product-category-select">
                                            <option value="">Select...</option>
                                            {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                                        </select>
                                        <div className="flex gap-2 mt-1.5">
                                            <input type="text" value={newCat} onChange={e => setNewCat(e.target.value)} placeholder="New category" className="form-input flex-1 text-xs" data-testid="new-category-input" />
                                            <button type="button" onClick={handleAddCategory} className="btn btn-secondary text-xs px-3" data-testid="add-category-btn">Add</button>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="form-label">Weight</label>
                                            <div className="grid grid-cols-3 gap-2">
                                                <input type="text" inputMode="decimal" value={form.weight} onChange={e => set('weight', e.target.value)} className="form-input col-span-2" placeholder="0.00" data-testid="product-weight-input" />
                                                <select value={form.weight_unit} onChange={e => set('weight_unit', e.target.value)} className="form-select" data-testid="product-weight-unit">
                                                    <option value="kg">kg</option><option value="g">g</option><option value="lb">lb</option><option value="pcs">pcs</option><option value="m">m</option><option value="lt">lt</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div><label className="form-label">Low Stock Alert</label><input type="number" value={form.low_stock_threshold} onChange={e => set('low_stock_threshold', e.target.value)} className="form-input" data-testid="product-threshold-input" /></div>
                                    </div>
                                    <div><label className="form-label">Boxes per Pallet</label><input type="number" value={form.boxes_per_pallet} onChange={e => set('boxes_per_pallet', e.target.value)} className="form-input" placeholder="e.g. 48" data-testid="product-boxes-per-pallet" /></div>
                                    <div><label className="form-label">Notes</label><textarea value={form.notes} onChange={e => set('notes', e.target.value)} className="form-input min-h-[80px] resize-y" rows={3} data-testid="product-notes-input" /></div>

                                    {/* Pack Based */}
                                    <div className="border border-gray-100 rounded-xl overflow-hidden">
                                        <div className="flex items-center justify-between p-4 bg-gray-50">
                                            <div className="flex items-center gap-2">
                                                <Package size={16} className="text-gray-400" />
                                                <span className="text-sm font-medium text-gray-700">Pack Based Product</span>
                                            </div>
                                            <button type="button" onClick={() => set('is_pack_based', !form.is_pack_based)}
                                                className={`relative w-10 h-5 rounded-full transition-colors ${form.is_pack_based ? 'bg-gray-900' : 'bg-gray-300'}`} data-testid="pack-based-toggle">
                                                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.is_pack_based ? 'translate-x-5' : 'translate-x-0.5'}`} />
                                            </button>
                                        </div>
                                        {form.is_pack_based && (
                                            <div className="p-4 space-y-3" data-testid="pack-content-section">
                                                <div>
                                                    <label className="form-label">Items per Pack</label>
                                                    <input type="number" value={form.pack_content} onChange={e => set('pack_content', e.target.value)} className="form-input" placeholder="e.g. 8" data-testid="pack-content-input" />
                                                </div>
                                                <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg">
                                                    <Info size={14} className="text-blue-500 mt-0.5 flex-shrink-0" />
                                                    <p className="text-xs text-blue-700">Pricing sekmesindeki fiyatlar paket fiyatıdır. Adet fiyatı otomatik hesaplanır: Adet Fiyatı = Paket Fiyatı / {packQty}</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* PRICING */}
                            {activeTab === 'pricing' && (
                                <div className="space-y-6" data-testid="tab-pricing-content">
                                    {isPack && (
                                        <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg" data-testid="pack-price-info">
                                            <Info size={14} className="text-blue-500 mt-0.5 flex-shrink-0" />
                                            <p className="text-xs text-blue-700">Bu ürün paket bazlıdır ({packQty} adet/paket). Girilen fiyatlar paket fiyatıdır, adet fiyatı otomatik hesaplanır.</p>
                                        </div>
                                    )}
                                    <div>
                                        <label className="form-label">{isPack ? 'Purchase Price (Pack)' : 'Purchase Price'}</label>
                                        <input type="number" step="0.01" value={form.purchase_price} onChange={e => set('purchase_price', e.target.value)} className="form-input" data-testid="purchase-price-input" />
                                        {isPack && form.purchase_price && (
                                            <p className="text-xs text-gray-500 mt-1">Unit: {unitPrice(form.purchase_price)} / pc</p>
                                        )}
                                    </div>
                                    <div className="border-t border-gray-100 pt-5">
                                        <label className="form-label mb-3">{isPack ? 'Sales Prices (Pack)' : 'Sales Prices'}</label>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-xs font-medium text-blue-600 mb-1 block">Price A</label>
                                                <input type="number" step="0.01" value={form.price_a} onChange={e => set('price_a', e.target.value)} className="form-input" data-testid="price-a-input" />
                                                {isPack && form.price_a && <p className="text-xs text-gray-500 mt-1">Unit: {unitPrice(form.price_a)} / pc</p>}
                                            </div>
                                            <div>
                                                <label className="text-xs font-medium text-blue-600 mb-1 block">Price B</label>
                                                <input type="number" step="0.01" value={form.price_b} onChange={e => set('price_b', e.target.value)} className="form-input" data-testid="price-b-input" />
                                                {isPack && form.price_b && <p className="text-xs text-gray-500 mt-1">Unit: {unitPrice(form.price_b)} / pc</p>}
                                            </div>
                                            <div>
                                                <label className="text-xs font-medium text-blue-600 mb-1 block">Price C</label>
                                                <input type="number" step="0.01" value={form.price_c} onChange={e => set('price_c', e.target.value)} className="form-input" data-testid="price-c-input" />
                                                {isPack && form.price_c && <p className="text-xs text-gray-500 mt-1">Unit: {unitPrice(form.price_c)} / pc</p>}
                                            </div>
                                            <div>
                                                <label className="text-xs font-medium text-gray-500 mb-1 block">Price D (Cost)</label>
                                                <div className="form-input bg-gray-100 text-gray-500 cursor-not-allowed">
                                                    {form.purchase_price ? parseFloat(form.purchase_price).toFixed(2) : '—'}
                                                </div>
                                                {isPack && form.purchase_price && <p className="text-xs text-gray-500 mt-1">Unit: {unitPrice(form.purchase_price)} / pc</p>}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* VARIANTS */}
                            {activeTab === 'variants' && !isNew && (
                                <div data-testid="tab-variants-content">
                                    <div className="flex items-center justify-end mb-4">
                                        <button onClick={() => openVariantModal()} className="btn btn-primary text-sm" data-testid="add-variant-btn"><Plus size={15} /> Varyant Ekle</button>
                                    </div>
                                    {variants.length > 0 ? (
                                        <div className="table-container">
                                            <table className="table">
                                                <thead><tr><th>Name</th><th>SKU</th><th>Barcode</th><th className="text-right">Price</th><th className="text-right">Stock</th><th className="w-20"></th></tr></thead>
                                                <tbody>
                                                    {variants.map(v => (
                                                        <tr key={v.id} data-testid={`variant-row-${v.id}`}>
                                                            <td className="font-medium text-gray-900">{v.name}</td>
                                                            <td className="text-gray-500">{v.sku || '—'}</td>
                                                            <td className="text-gray-500 font-mono text-xs">{v.barcode || '—'}</td>
                                                            <td className="text-right font-medium">{v.price != null ? v.price.toFixed(2) : '—'}</td>
                                                            <td className="text-right font-medium">{v.current_stock}</td>
                                                            <td>
                                                                <div className="flex items-center gap-1 justify-end">
                                                                    <button onClick={() => openVariantModal(v)} className="btn-icon" data-testid={`edit-variant-${v.id}`}><Edit2 size={14} /></button>
                                                                    <button onClick={() => handleDeleteVariant(v.id)} className="btn-icon text-red-400 hover:text-red-600 hover:bg-red-50" data-testid={`delete-variant-${v.id}`}><Trash2 size={14} /></button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="text-center py-10 text-gray-400 text-sm">No variants yet</div>
                                    )}
                                </div>
                            )}

                            {/* HISTORY */}
                            {activeTab === 'history' && !isNew && (
                                <div data-testid="tab-history-content">
                                    <h3 className="text-sm font-semibold text-gray-900 mb-4">Son 10 Stok Hareketi</h3>
                                    {movements.length > 0 ? (
                                        <div className="space-y-2">
                                            {movements.slice(0, 10).map(m => (
                                                <div key={m.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${m.movement_type === 'IN' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                                                            {m.movement_type === 'IN' ? '+' : '-'}
                                                        </div>
                                                        <div>
                                                            <div className="text-sm font-medium text-gray-900">{m.employee_name}</div>
                                                            <div className="text-xs text-gray-400">{m.warehouse_name} · {new Date(m.created_at).toLocaleDateString('tr-TR')}</div>
                                                        </div>
                                                    </div>
                                                    <span className={`text-sm font-semibold ${m.movement_type === 'IN' ? 'text-green-600' : 'text-red-600'}`}>
                                                        {m.movement_type === 'IN' ? '+' : '-'}{m.quantity}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-400 text-center py-8">No movements found</p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Variant Modal */}
                <Dialog open={showVariantModal} onOpenChange={setShowVariantModal}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle>{editingVariant ? 'Edit Variant' : 'New Variant'}</DialogTitle>
                            <DialogDescription>Define variant properties</DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleVariantSubmit} className="space-y-4 mt-4">
                            <div><label className="form-label">Name *</label><input type="text" value={variantForm.name} onChange={e => setVariantForm(p => ({...p, name: e.target.value}))} className="form-input" required data-testid="variant-name-input" placeholder="e.g. Red / Large / 250ml" /></div>
                            <div className="grid grid-cols-2 gap-3">
                                <div><label className="form-label">SKU</label><input type="text" value={variantForm.sku} onChange={e => setVariantForm(p => ({...p, sku: e.target.value}))} className="form-input" data-testid="variant-sku-input" /></div>
                                <div><label className="form-label">Barcode</label><input type="text" value={variantForm.barcode} onChange={e => setVariantForm(p => ({...p, barcode: e.target.value}))} className="form-input" data-testid="variant-barcode-input" /></div>
                            </div>
                            <div><label className="form-label">Price Override</label><input type="number" step="0.01" value={variantForm.price} onChange={e => setVariantForm(p => ({...p, price: e.target.value}))} className="form-input" placeholder="Leave empty to use base price" data-testid="variant-price-input" /></div>
                            <div className="flex gap-3 pt-2">
                                <button type="button" onClick={() => setShowVariantModal(false)} className="btn btn-secondary flex-1" disabled={variantSaving}>Cancel</button>
                                <button type="submit" className="btn btn-primary flex-1" disabled={variantSaving} data-testid="save-variant-btn">{variantSaving ? 'Saving...' : editingVariant ? 'Update' : 'Create'}</button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
}
